if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.setupMobileLightboxToggle();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.thumbnailList = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
        this.thumbnailPrevButton = this.elements.thumbnails.querySelector('.slider-button--prev');
        this.thumbnailNextButton = this.elements.thumbnails.querySelector('.slider-button--next');
        this.onThumbnailResize = debounce(this.updateThumbnailOverflowState.bind(this), 150);
        this.onThumbnailScroll = debounce(this.updateThumbnailArrowState.bind(this), 50);

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', (event) => {
            this.onThumbnailClick(event, mediaToSwitch.dataset.target);
          });
        });
        this.bindThumbnailArrowEvents();
        this.updateThumbnailOverflowState();
        window.addEventListener('resize', this.onThumbnailResize);
        this.thumbnailList?.addEventListener('scroll', this.onThumbnailScroll);
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onThumbnailResize);
        this.thumbnailList?.removeEventListener('scroll', this.onThumbnailScroll);
        this.mobileLightboxMql?.removeEventListener('change', this.onMobileLightboxChange);
      }

      setupMobileLightboxToggle() {
        this.lightboxToggles = this.querySelectorAll('.product__media-zoom-lightbox');
        if (this.lightboxToggles.length === 0) return;
        this.mobileLightboxMql = window.matchMedia('(max-width: 749px)');
        this.onMobileLightboxChange = this.onMobileLightboxChange.bind(this);
        this.mobileLightboxMql.addEventListener('change', this.onMobileLightboxChange);
        this.onMobileLightboxChange();
      }

      onMobileLightboxChange() {
        const disable = this.mobileLightboxMql.matches;
        this.lightboxToggles.forEach((btn) => {
          btn.tabIndex = disable ? -1 : 0;
          if (disable) {
            btn.setAttribute('aria-hidden', 'true');
          } else {
            btn.removeAttribute('aria-hidden');
          }
        });
      }

      onThumbnailClick(event, mediaId) {
        event.preventDefault();
        event.stopPropagation();
        this.setActiveMedia(mediaId, false);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        const previousActiveMedia = this.elements.viewer.querySelector('.is-active[data-media-id]');
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.animateMediaTransition(previousActiveMedia, activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      animateMediaTransition(previousActiveMedia, activeMedia) {
        if (!activeMedia || previousActiveMedia === activeMedia) return;
        activeMedia.classList.remove('product__media-item--fade-in');
        // Force reflow so the animation can replay.
        void activeMedia.offsetWidth;
        activeMedia.classList.add('product__media-item--fade-in');
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.updateThumbnailArrowState();
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
        this.updateThumbnailArrowState();
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      bindThumbnailArrowEvents() {
        if (!this.thumbnailList || !this.thumbnailPrevButton || !this.thumbnailNextButton) return;

        this.thumbnailPrevButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.scrollThumbnailStrip(-1);
        });

        this.thumbnailNextButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.scrollThumbnailStrip(1);
        });
      }

      scrollThumbnailStrip(direction) {
        if (!this.thumbnailList) return;
        const scrollStep = Math.max(this.thumbnailList.clientWidth * 0.75, 120);
        this.thumbnailList.scrollBy({
          left: direction * scrollStep,
          behavior: 'smooth',
        });
        window.setTimeout(() => this.updateThumbnailArrowState(), 220);
      }

      updateThumbnailOverflowState() {
        if (!this.thumbnailList || !this.elements.thumbnails) return;
        const hasOverflow = this.thumbnailList.scrollWidth - this.thumbnailList.clientWidth > 4;
        this.elements.thumbnails.classList.toggle('thumbnail-slider--overflow', hasOverflow);
        if (!hasOverflow) {
          this.thumbnailList.scrollLeft = 0;
        }
        this.updateThumbnailArrowState();
      }

      updateThumbnailArrowState() {
        if (!this.thumbnailList || !this.thumbnailPrevButton || !this.thumbnailNextButton) return;
        const maxScrollLeft = this.thumbnailList.scrollWidth - this.thumbnailList.clientWidth;
        const currentScrollLeft = this.thumbnailList.scrollLeft;
        this.thumbnailPrevButton.disabled = currentScrollLeft <= 2;
        this.thumbnailNextButton.disabled = currentScrollLeft >= maxScrollLeft - 2;
      }
    }
  );
}
